import { useMemo } from 'react';
import { MANIFESTS } from '../content';
import { validate } from '../validate';
import type { Result } from '../validate';

const BROKEN = `kind: Service
name: Broken-API
account: 111122223333
region: us-east-1

cluster:
  name: my-cluster

network:
  vpcId: vpc-0abc123def4567890
  subnets: []

task:
  cpu: 256
  memory: 4096

loadBalancer:
  loadBalancerArn: arn:aws:elasticloadbalancing:us-east-1:111122223333:loadbalancer/app/my-alb/1234567890abcdef
  listenerProtocol: HTTPS
  securityGroupId: sg-0abc123def4567890
  hostHeaders: broken.example.com
  healthCheck:
    intervalSeconds: 10
    timeoutSeconds: 30
`;

/**
 * The manifest validator.
 *
 * Runs the action's own `validateManifest` in the browser, so the errors here
 * are the errors CI would print — no server, no AWS credentials, nothing
 * uploaded.
 */
export function Validator({
  text,
  onChange,
}: {
  text: string;
  onChange: (yaml: string) => void;
}) {
  // Cheap enough for every keystroke: pure string and object work.
  const result = useMemo(() => validate(text), [text]);

  return (
    <section id="validator">
      <h2>Check a manifest, right here</h2>
      <p className="lede">
        This runs the deployer's own validator, compiled to run in your browser. Nothing is
        uploaded, and the messages are identical to the ones CI prints.
      </p>

      <div className="tabs" role="group" aria-label="Load an example">
        {MANIFESTS.map((file) => (
          <button key={file.id} type="button" className="tab" onClick={() => onChange(file.body)}>
            {file.name}
          </button>
        ))}
        <button type="button" className="tab tab-warn" onClick={() => onChange(BROKEN)}>
          a broken one
        </button>
      </div>

      <div className="validator">
        <textarea
          value={text}
          spellCheck={false}
          aria-label="Manifest YAML"
          onChange={(event) => onChange(event.target.value)}
        />
        <ResultPanel result={result} />
      </div>

      <p className="small muted" style={{ marginTop: '1rem' }}>
        Validation stops short of a plan — working out what would actually change needs AWS
        credentials. For that, run{' '}
        <code>npx fargate-deployer diff --manifest deploy/production.yaml --image …</code>
      </p>
    </section>
  );
}

function ResultPanel({ result }: { result: Result }) {
  if (result.status === 'empty') {
    return (
      <div className="panel">
        <p className="muted small">Paste a manifest to check it.</p>
      </div>
    );
  }

  if (result.status === 'unparseable') {
    return (
      <div className="panel bad">
        <div className="status bad">✗ Not valid YAML</div>
        <pre className="small" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
          {result.message}
        </pre>
      </div>
    );
  }

  if (result.status === 'invalid') {
    return (
      <div className="panel bad">
        <div className="status bad">
          ✗ {result.issues.length} problem{result.issues.length === 1 ? '' : 's'}
        </div>
        {result.issues.map((issue, i) => (
          <div className="issue" key={i}>
            <div className="issue-path">{issue.path}</div>
            <div className="issue-msg">{issue.message}</div>
            {issue.hint && <div className="issue-hint">{issue.hint}</div>}
          </div>
        ))}
      </div>
    );
  }

  const { summary } = result;
  return (
    <div className="panel ok">
      <div className="status ok">✓ Valid</div>
      <div className="summary-row">
        <span>kind</span>
        <span>{summary.kind}</span>
      </div>
      <div className="summary-row">
        <span>name</span>
        <span>
          <code>{summary.name}</code>
        </span>
      </div>
      {summary.detail.map((line, i) => (
        <div className="summary-row" key={i}>
          <span>{i === 0 ? 'target' : ''}</span>
          <span>{line}</span>
        </div>
      ))}
    </div>
  );
}
